import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PublicFooter } from '../../components/public/PublicFooter.js';
import { PublicNav } from '../../components/public/PublicNav.js';

const LAST_UPDATED = 'October 11, 2026';

export function PrivacyPolicyPage() {
  useEffect(() => {
    document.title = 'Privacy — ScrubLedger';
  }, []);

  return (
    <div className="public-page">
      <PublicNav />
      <main className="public-simple-page public-simple-page--legal">
        <article className="public-legal">
          <h1>Privacy Policy</h1>
          <p className="public-legal__updated">Last updated: {LAST_UPDATED}</p>

          <section>
            <h2>Introduction</h2>
            <p>
              ScrubLedger provides clinical case tracking and clinical-document workflow software for
              educational programs, including Surgical Technology programs. This Privacy Policy describes how
              ScrubLedger collects, uses, stores, and protects information when you visit our public
              website, request a demo, or use an authenticated program workspace operated by your
              institution.
            </p>
            <p>
              Your institution may have additional policies governing student records, clinical
              documentation, and workforce data. Where those policies apply, they control how your
              institution uses ScrubLedger on your behalf.
            </p>
          </section>

          <section>
            <h2>Information we collect</h2>
            <p>
              The information we process depends on how you interact with ScrubLedger. We collect only what
              is needed to operate the service, respond to inquiries, authenticate users, and support
              program workflows configured by your institution.
            </p>
          </section>

          <section>
            <h2>Account information</h2>
            <p>
              When your institution provisions access, we store account details such as your name, email
              address, password hash (we do not store plaintext passwords), account status, and
              role-based program memberships. Sign-in uses a server-side session identified by an
              httpOnly cookie; session tokens are stored only as cryptographic hashes in our database.
            </p>
          </section>

          <section>
            <h2>Institution and program information</h2>
            <p>
              We store organizational and program configuration data your institution enters or that
              platform administrators provision, such as organization and program names, cohorts, clinical
              sites, specialties, procedures, graduation requirements, and membership assignments linking
              users to programs and roles (for example, student, instructor, or program director).
            </p>
          </section>

          <section>
            <h2>Clinical documentation and academic records</h2>
            <p>
              Users may upload or capture clinical documentation (such as clinical experience sheets),
              associated metadata, extracted case fields, faculty review decisions, and verified academic
              ledger entries tied to program requirements. This content is stored in tenant-isolated
              program data and private object storage; access is enforced by server-side authorization
              based on program membership and role.
            </p>
          </section>

          <section>
            <h2>Demo and contact requests</h2>
            <p>
              If you submit a demo or contact request on our public website, we collect the information
              you provide (such as name, institution, work email, and optional phone, role, program, and
              message) and deliver it to our team by email so we can respond. We do not use those
              submissions to create a program account unless we separately agree with your institution.
            </p>
          </section>

          <section>
            <h2>Technical, session, and security information</h2>
            <p>
              We process technical data needed to secure and operate the service, such as HTTP request
              metadata, correlation identifiers, rate-limit counters, processing job telemetry, and
              structured application logs. Access logs are designed to avoid recording sensitive request
              bodies or clinical file contents.
            </p>
          </section>

          <section>
            <h2>How we use information</h2>
            <ul>
              <li>Provide, maintain, and improve ScrubLedger for authorized users and institutions</li>
              <li>Authenticate users, enforce role-based access, and maintain tenant isolation</li>
              <li>Process clinical documents, extraction workflows, and faculty review as configured</li>
              <li>Respond to demo requests, support inquiries, and operational alerts</li>
              <li>Protect the service against abuse, fraud, and security threats</li>
            </ul>
          </section>

          <section>
            <h2>Authentication and session handling</h2>
            <p>
              Authenticated API requests rely on session cookies scoped to the API host. State-changing
              requests from the web application require a matching browser Origin header. Sessions expire
              after a fixed period from creation. You should sign out on shared devices and protect your
              credentials.
            </p>
          </section>

          <section>
            <h2>Document and data storage</h2>
            <p>
              Relational data is stored in PostgreSQL. Clinical files and derivatives are stored in private
              object storage configured for the deployment (local storage in development and test;
              S3-compatible storage in production). Document processing may invoke configured extraction
              providers when enabled by the deployment; transmission to external OCR providers is controlled
              by environment configuration and is disabled unless explicitly enabled for a given environment.
            </p>
          </section>

          <section>
            <h2>Service providers and infrastructure</h2>
            <p>
              We use hosting, database, object storage, email delivery, and (when enabled) document
              intelligence providers to run ScrubLedger. Those providers process data on our instructions
              to deliver the service. We do not sell personal information collected through the product.
            </p>
          </section>

          <section>
            <h2>Security practices</h2>
            <p>
              We use industry-standard measures appropriate to the service, including encrypted transport
              (HTTPS), hashed passwords, hashed session tokens, tenant-scoped data access, private storage
              for clinical files, and rate limiting on sensitive endpoints. No system is perfectly secure;
              we work to reduce risk and address issues responsibly.
            </p>
          </section>

          <section>
            <h2>Data retention and deletion</h2>
            <p>
              Clinical source documents, derivatives, processing artifacts, audit events, and ledger entries
              may be retained according to deployment configuration and institutional needs. The product can
              surface retention planning and dry-run reporting for clinical source documents when configured,
              but automatic deletion of clinical source documents is not implemented in the current release;
              applying deletion remains unavailable until institutional policy and product support for it are
              in place. Institutions are responsible for their own records-management practices and for
              requesting export or handling through program administrators and support channels where
              applicable.
            </p>
          </section>

          <section>
            <h2>Institution and user responsibilities</h2>
            <p>
              Institutions choose who receives access, configure program requirements, and determine what
              clinical documentation students and faculty submit. Users must provide accurate information,
              use credentials only as authorized, and follow institutional policies and applicable laws
              governing educational and clinical records.
            </p>
          </section>

          <section>
            <h2>Students and minors</h2>
            <p>
              ScrubLedger is intended for use by accredited educational programs and their faculty and
              students. Student accounts are created and managed by the institution. If you believe a minor
              has provided personal information to us without appropriate institutional authorization,
              contact us so we can assist with next steps.
            </p>
          </section>

          <section>
            <h2>Policy updates</h2>
            <p>
              We may update this Privacy Policy from time to time. We will post the revised policy on this
              page and update the &quot;Last updated&quot; date. Continued use of the service after changes
              become effective constitutes acceptance of the updated policy where permitted by law.
            </p>
          </section>

          <section>
            <h2>Contact</h2>
            <p>
              Questions about this Privacy Policy or our data practices:{' '}
              <a href="mailto:support@scrubledger.com">support@scrubledger.com</a>.
            </p>
          </section>

          <p className="public-legal__back">
            <Link to="/" className="btn btn--ghost">Back to home</Link>
          </p>
        </article>
      </main>
      <PublicFooter />
    </div>
  );
}
